import romanApi from "@roman/shared/lib/net/romanApi";
import type { PredictionMarketHistory, PredictionMarketWithProbability } from "@roman/shared/types/PredictionMarket";
import type { SWRConfiguration } from "swr";
import useSWR from "swr";

export type PredictionMarketDetailsProps = {
  predictionMarketId?: string;
  swrOptions?: SWRConfiguration;
  fetcher?: (url: string, predictionMarketId: string) => Promise<PredictionMarketDetailsResponse>;
  /**
   * Optional cache bust key to force refetch.
   * When this value changes, SWR will refetch the data.
   */
  cacheBustKey?: number;
};

export type PredictionMarketDetailsResponse = {
  predictionMarket: PredictionMarketWithProbability;
  numberOfParticipants: number;
  totalEnteredFreePoints: number;
  predictionMarketHistory: PredictionMarketHistory;
};

export const createDefaultPredictionMarketHistoryV2Fetcher = () => {
  return (url: string, predictionMarketId: string) => {
    return romanApi.get<PredictionMarketDetailsResponse>(`${url}/${predictionMarketId}`);
  };
};

export const useGetPredictionMarketDetails = ({
  predictionMarketId,
  swrOptions,
  fetcher,
  cacheBustKey,
}: PredictionMarketDetailsProps) => {
  const historyFetcher = fetcher || createDefaultPredictionMarketHistoryV2Fetcher();
  const swrKey = predictionMarketId?.trim() ? ["/prediction-market/details", predictionMarketId, cacheBustKey] : null;

  const swrQuery = useSWR<PredictionMarketDetailsResponse>(swrKey, ([url, id]) => historyFetcher(url, id), swrOptions);

  return swrQuery;
};
